import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ForwardIcon,
  Loader2,
  ShoppingCart,
  MapPin,
  RefreshCw,
  Briefcase,
  Home,
  Layers,
  Trash2,
  ShoppingBag,
  Sparkles,
  Plus,
  Equal,
  Edit2,
} from "lucide-react";
import ItemsComp from "../../../components/customer/CartComponents.tsx/ItemsComp";
import CouponCard from "../../../components/customer/CartComponents.tsx/CouponCard";
import OrderSummary from "../../../components/customer/CartComponents.tsx/OrderSummary";
import RewardPointsCard from "../../../components/customer/CartComponents.tsx/RewardPointsCard";
import DonateCard from "../../../components/customer/CartComponents.tsx/DonateCard";
import FreeClaimComponent from "../../../components/customer/CartComponents.tsx/FreeClaimComponent";
import CommonButton from "../../../components/CommonButton";
import { AddressForm } from "../../../components/Address/AddressForm";

import { ProductItem } from "../../../utils/Customer/CustomerTypes";

import {
  useLazyGetCheckoutSummaryQuery,
  useApplyCouponMutation,
  useApplyRewardPointsMutation,
  useSelectFreeGiftMutation,
  useUpdateDonationMutation,
  useLazyRemoveCouponQuery,
  useLazyRemoveRewardPointsQuery,
  useLazyRemoveFreeGiftQuery,
  useProcessToCheckoutMutation,
  useSelectAddressMutation,
  useIncreaseProductQuantityMutation,
  useDecreaseProductQuantityMutation,
  useLazyRemoveProductQuery,
  useLazyRemovePetQuery,
  useLazyRemoveServiceQuery,
  useApplyWalletMutation,
  useLazyRemoveWalletQuery,
  useLazyGetTopUpOfferQuery,
  useAddTopupOfferMutation,
  useCreateCartMutation,
  useCompleteInternalPaymentMutation,
  useAddBundleToCartMutation,
  useUpdateCartSubscriptionMutation,
  useUpdatePetPurchaseTypeMutation,
} from "../../../redux/actions/Customer/cartCustomer";
import { useLazyGetAddressQuery } from "../../../redux/actions/Customer/profileCustomer";

import {
  BundleProduct,
  IAddressData,
} from "../../../types/Response/CustomerResponse";
import { useAlert } from "../../../components/customer/AlertContext";
import WalletCard from "../../../components/customer/CartComponents.tsx/WalletCard";
import ProductGridSection from "../../../components/customer/ProductGridSection";
import LoginModal from "../../../components/customer/LoginModal";
import { useToast } from "../../../utils/useToast";
import ToastContainer from "../../../utils/ToastContainer";
import { AddBundleToCartRequest } from "../../../types/Request/CustomerRequest";

export interface IBundleItem {
  cartItemId: number;
  productVariantId: number;
  productName: string;
  variantName: string;
  imageUrl: string;
  quantity: number;
  unitPrice: number;
  unitSalePrice: number;
  total: number;
  inStock: boolean;
  isPromotional: boolean;
  isFreeItem: boolean;
  isMandatory: boolean;
  isAutoAdded: boolean;
  promotionId: number | null;
  productId: number;
  isSubscription: boolean;
  billingCycle: number;
  interval: number;
  isBundleItem: boolean;
  productBundleId: number | null;
  bundleUnitPrice: number | null;
  bundleQuantity: number;
}

export interface IBundle {
  bundleId: number;
  name: string;
  imageUrl: string;
  bundlePrice: number;
  originalPrice: number;
  savingAmount: number;
  items: IBundleItem[];
  totalItems: number;
  total: number;
}

interface IBundleSuggestion {
  bundleId: number;
  name: string;
  bundlePrice: number;
  originalPrice: number;
  savingAmount: number;
  description: string;
  imageUrl: string;
  alreadyAdded: boolean;
  products: any[];
}

const mapToProductItem = (item: any): ProductItem => ({
  productid: item.productId.toString(),
  ProductId: item.productId,
  sku: item.sku ?? "",
  category: "",
  subcategory: "",
  innerCategory: "",
  productname: item.name,
  brand: item.brandName ?? "",
  saleprice: (item.salePrice ?? item.price).toString(),
  price: item.price.toString(),
  startDate: item.saleStartDate ?? "",
  endDate: item.saleEndDate ?? "",
  productimage: item.imageUrl || "",
  subimage: [],
  quantity: 10,
  productstatus: !item.alreadyInCart,
  video: [],
  isSubscribe: false,
  description: "",
  keyFeatures: [],
  isReward: false,
});

const CustomerCart: React.FC = () => {
  const navigate = useNavigate();
  const { showConfirm } = useAlert();
  const [addressView, setAddressView] = useState<"list" | "form">("list");
  const addressSectionRef = useRef<HTMLDivElement>(null);
  const [highlightAddress, setHighlightAddress] = useState(false);
  const [addressError, setAddressError] = useState("");
  const { toasts, addToast, removeToast } = useToast();
  const [activeSelectedAddress, setActiveSelectedAddress] =
    useState<IAddressData | null>(null);

  const [
    triggerGetSummary,
    {
      data: summaryResponse,
      isFetching: isFetchingSummary,
      isUninitialized: isSummaryUninitialized,
      error
    },
  ] = useLazyGetCheckoutSummaryQuery();
  
  const [
    triggerGetSavedAddresses,
    { data: savedAddressesResponse, isFetching: isFetchingAddresses },
  ] = useLazyGetAddressQuery();
  const [selectAddressMapping, { isLoading: isLinkingAddress }] =
    useSelectAddressMutation();
  const [createCart] = useCreateCartMutation();
  const [applyCoupon, { isLoading: isApplyingCoupon }] =
    useApplyCouponMutation();
  const [applyRewardPoints, { isLoading: isApplyingRewards }] =
    useApplyRewardPointsMutation();
  const [selectFreeGift, { isLoading: isSelectingGift }] =
    useSelectFreeGiftMutation();
  const [updateDonation, { isLoading: isUpdatingDonation }] =
    useUpdateDonationMutation();
  const [processToCheckout, { isLoading: isProcessingOrder }] =
    useProcessToCheckoutMutation();

  const [triggerRemoveCoupon, { isFetching: isRemovingCoupon }] =
    useLazyRemoveCouponQuery();
  const [triggerRemoveRewards, { isFetching: isRemovingRewards }] =
    useLazyRemoveRewardPointsQuery();
  const [triggerRemoveGift, { isFetching: isRemovingGift }] =
    useLazyRemoveFreeGiftQuery();
  const [applyWallet, { isLoading: isApplyingWallet }] =
    useApplyWalletMutation();
  const [triggerRemoveWallet, { isFetching: isRemovingWallet }] =
    useLazyRemoveWalletQuery();
  const [AddBundleToCart] = useAddBundleToCartMutation();
  // Lazy endpoints instantiation for runtime package processing
  const [
    triggerGetTopUpOffers,
    { data: topUpOffersList, isFetching: isFetchingOffers },
  ] = useLazyGetTopUpOfferQuery();
  const [addTopupOffer, { isLoading: isInitializingTopUp }] =
    useAddTopupOfferMutation();

  const [increaseQty, { isLoading: isIncreasingQty }] =
    useIncreaseProductQuantityMutation();
  const [decreaseQty, { isLoading: isDecreasingQty }] =
    useDecreaseProductQuantityMutation();
  const [triggerRemoveProduct, { isFetching: isRemovingProduct }] =
    useLazyRemoveProductQuery();
  const [triggerRemovePet, { isFetching: isRemovingPet }] =
    useLazyRemovePetQuery();
  const [triggerRemoveService, { isFetching: isRemovingService }] =
    useLazyRemoveServiceQuery();
  const [
    updateCartSubscription,
    {
      isLoading: isSubscriptionUpdating,
    },
  ] = useUpdateCartSubscriptionMutation();

  const [
    updatePetPurchaseType,
    {
      isLoading: isPetPurchaseTypeUpdating,
    },
  ] = useUpdatePetPurchaseTypeMutation();

  const [productItems, setProductItems] = useState<any[]>([]);
  const [bundleItems, setBundleItems] = useState<IBundle[]>([]);
  const [petItems, setPetItems] = useState<any[]>([]);
  const [serviceItems, setServiceItems] = useState<any[]>([]);

  const [forcedItems, setForcedItems] = useState<any[]>([]);
  const [selectedFreeGifts, setSelectedFreeGifts] = useState<any[]>([]);
  const [showGiftAnimation, setShowGiftAnimation] = useState(false);

  const [similarProducts, setSimilarProducts] = useState<ProductItem[]>([]);
  const [sponsoredProducts, setSponsoredProducts] = useState<ProductItem[]>([]);
  const [recommendedProducts, setRecommendedProducts] = useState<ProductItem[]>(
    [],
  );
  const [suggestedBundles, setSuggestedBundles] = useState<IBundleSuggestion[]>(
    [],
  );
  const [selectedAddressId, setSelectedAddressId] = useState<
    number | undefined
  >(undefined);
  const [completeInternalPayment, { isLoading: isCompletingInternalPayment }] =
    useCompleteInternalPaymentMutation();
  const [isSubmittingCheckout, setIsSubmittingCheckout] = useState(false);
  const isGlobalProcessing =
    isFetchingSummary ||
    isFetchingAddresses ||
    isLinkingAddress ||
    isApplyingCoupon ||
    isApplyingRewards ||
    isSelectingGift ||
    isUpdatingDonation ||
    isRemovingCoupon ||
    isRemovingRewards ||
    isRemovingGift ||
    isProcessingOrder ||
    isIncreasingQty ||
    isDecreasingQty ||
    isRemovingProduct ||
    isRemovingPet ||
    isRemovingService ||
    isApplyingWallet ||
    isRemovingWallet ||
    isInitializingTopUp ||
    isSubscriptionUpdating ||
    isPetPurchaseTypeUpdating ||
    isCompletingInternalPayment;
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginMessage, setLoginMessage] = useState("");
  const [isGuestVerification, setIsGuestVerification] = useState(false);

  //const [guestVerificationPhone, setGuestVerificationPhone] = useState("");
  const [guestVerificationEmail, setGuestVerificationEmail] = useState("");
  useEffect(() => {
    triggerGetSummary();
    triggerGetSavedAddresses();
  }, [triggerGetSummary, triggerGetSavedAddresses]);

  useEffect(() => {
    if (summaryResponse?.cart) {
      setProductItems(summaryResponse.cart.productCart?.items ?? []);
      setPetItems(summaryResponse.cart.petCart?.items ?? []);
      setServiceItems(summaryResponse.cart.serviceCart?.items ?? []);

      const rawBundles = summaryResponse.cart.productCart?.bundles;
      if (Array.isArray(rawBundles)) {
        setBundleItems(rawBundles as IBundle[]);
      } else if (rawBundles && typeof rawBundles === "object") {
        setBundleItems([rawBundles] as IBundle[]);
      } else {
        setBundleItems([]);
      }
    }

    if (summaryResponse?.forcedProducts) {
      setForcedItems(summaryResponse.forcedProducts);
    } else {
      setForcedItems([]);
    }

    if (summaryResponse?.claimableGifts) {
      const selectedGifts = summaryResponse.claimableGifts.filter(
        (g: any) => g.isSelected,
      );
      setSelectedFreeGifts(selectedGifts);
    } else {
      setSelectedFreeGifts([]);
    }

    setSimilarProducts(
      (summaryResponse?.similarProducts ?? []).map(mapToProductItem),
    );
    setSponsoredProducts(
      (summaryResponse?.sponsoredProducts ?? []).map(mapToProductItem),
    );
    setRecommendedProducts(
      (summaryResponse?.recommendedProducts ?? []).map(mapToProductItem),
    );
    setSuggestedBundles(summaryResponse?.bundleSuggestions ?? []);
  }, [summaryResponse]);

  useEffect(() => {
    const addresses = savedAddressesResponse?.data || [];

    if (addresses.length > 0) {
      const primaryDefault =
        addresses.find((a: any) => a.isDefault) || addresses[0];

      setActiveSelectedAddress(primaryDefault);
      setAddressView("list");
    } else {
      setActiveSelectedAddress(null);
      setAddressView("form");
    }
  }, [savedAddressesResponse]);

  const pricing = summaryResponse?.pricing;
  const savedAddressesList = savedAddressesResponse?.data || [];

  const totalItemsCount =
    productItems.length +
    bundleItems.length +
    petItems.length +
    serviceItems.length +
    forcedItems.length;
  useEffect(() => {
    // Do nothing until the checkout summary API has actually been executed.
    if (isSummaryUninitialized) {
      return;
    }

    // Do not evaluate the cart while the summary API is loading.
    if (isFetchingSummary) {
      return;
    }

    // Wait until we have a valid checkout summary response.
    if (!summaryResponse?.cart) {
      return;
    }

    const productCount = summaryResponse.cart.productCart?.items?.length ?? 0;

    const bundleCount = Array.isArray(summaryResponse.cart.productCart?.bundles)
      ? summaryResponse.cart.productCart.bundles.length
      : summaryResponse.cart.productCart?.bundles
        ? 1
        : 0;

    const petCount = summaryResponse.cart.petCart?.items?.length ?? 0;

    const serviceCount = summaryResponse.cart.serviceCart?.items?.length ?? 0;

    const forcedProductCount = summaryResponse.forcedProducts?.length ?? 0;

    const totalCartItems =
      productCount + bundleCount + petCount + serviceCount + forcedProductCount;

    // Only redirect when the API confirms that the cart is genuinely empty.
    if (totalCartItems === 0) {
      navigate("/productpage/productListingScreen", {
        state: {
          source: "AllProducts",
          data: {},
        },
        replace: true,
      });
    }
  }, [isSummaryUninitialized, isFetchingSummary, summaryResponse, navigate]);
  const handleIncreaseQuantity = async (cartItemId: number) => {
    try {
      await increaseQty(cartItemId).unwrap();
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        "Could not increase quantity.";
      showConfirm({
        title: "",
        message: errorMessage,
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleDecreaseQuantity = async (
    cartItemId: number,
    currentQty: number,
  ) => {
    if (currentQty <= 1) {
      handleRemoveProductItem(cartItemId);
      return;
    }
    try {
      await decreaseQty(cartItemId).unwrap();
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        "Could not decrease quantity.";
      showConfirm({
        title: "Error Updating Quantity",
        message: errorMessage,
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleRemoveProductItem = async (cartItemId: number) => {
    try {
      await triggerRemoveProduct(cartItemId).unwrap();
      triggerGetSummary();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveBundleGroup = async (bundle: IBundle) => {
    try {
      const bundleItems = bundle.items ?? [];

      console.log("🗑️ Removing bundle:", bundle.bundleId);
      console.log(
        "🗑️ Bundle cart items:",
        bundleItems.map((x) => ({
          cartItemId: x.cartItemId,
          productId: x.productId,
          bundleId: x.productBundleId,
        })),
      );

      if (bundleItems.length === 0) {

        return;
      }

      for (const item of bundleItems) {
        if (!item.cartItemId || item.cartItemId <= 0) {
          continue;
        }

        console.log(`🗑️ Removing CartItem ${item.cartItemId}`);

        await triggerRemoveProduct(item.cartItemId).unwrap();
      }

      console.log("✅ Bundle removed successfully");

      await triggerGetSummary();
    } catch (err: any) {
      console.error("❌ Failed to remove bundle:", err);

      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        "Could not remove the bundle from your cart.";

      showConfirm({
        title: "Remove Bundle",
        message: errorMessage,
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleRemovePetItem = async (petCartItemId: number) => {
    try {
      await triggerRemovePet(petCartItemId).unwrap();
      triggerGetSummary();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveServiceItem = async (cartItemId: number) => {
    showConfirm({
      title: "Remove Service",
      message: "Are you sure you want to remove this service booking?",
      type: "warning",
      confirmText: "Remove",
      onConfirm: async () => {
        try {
          await triggerRemoveService(cartItemId).unwrap();
          triggerGetSummary();
        } catch (err) {
          console.error(err);
        }
      },
    });
  };

  const handleAddBundleToCart = async (bundleId: number) => {
    try {
      const request: AddBundleToCartRequest = {
        bundleId: bundleId,
      };
      await AddBundleToCart(request).unwrap();

      await triggerGetSummary();
    } catch (err: any) {
      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        "Could not decrease quantity.";
      showConfirm({
        title: "Error Updating Quantity",
        message: errorMessage,
        type: "error",
        confirmText: "Ok",
      });
    }
  };
  useEffect(() => {
    // Check if the user returned from a successful wallet top-up session
    const topUpSuccess = sessionStorage.getItem("wallet_topup_success_flag");
    const topUpAmount = sessionStorage.getItem("wallet_topup_amount");

    if (topUpSuccess === "true") {
      // Clear flags immediately to avoid duplicate alerts on manual page reloads
      sessionStorage.removeItem("wallet_topup_success_flag");
      sessionStorage.removeItem("wallet_topup_tx_ref");
      sessionStorage.removeItem("wallet_topup_amount");
      sessionStorage.removeItem("wallet_topup_credit");

      // Force a summary refresh to ensure the newly added wallet funds show up immediately
      triggerGetSummary();

      // Display the success confirmation alert box
      showConfirm({
        title: "Top-Up Successful!",
        message: topUpAmount
          ? `Successfully deposited AED ${Number(topUpAmount).toFixed(
            2,
          )} into your wallet balance.`
          : "Your wallet funds have been successfully updated.",
        type: "success",
        confirmText: "Awesome",
      });
    }
  }, [triggerGetSummary, showConfirm]);

  const handleSelectAlternativeAddress = async (addr: IAddressData) => {
    try {
      await selectAddressMapping({ addressId: addr.id }).unwrap();
      setActiveSelectedAddress(addr);
      setAddressError("");
      setAddressView("list");
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
      showConfirm({
        title: "Address Error",
        message: err?.data?.message || "Failed to switch address.",
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleFormSuccessCompletion = async () => {
    try {
      const addressesRefresh = await triggerGetSavedAddresses().unwrap();
      const addressDataList = addressesRefresh?.data || [];

      if (addressDataList.length > 0) {
        const targetAddress =
          addressDataList.find((a: any) => a.isDefault) ||
          addressDataList[addressDataList.length - 1];
        await selectAddressMapping({ addressId: targetAddress.id }).unwrap();
        setActiveSelectedAddress(targetAddress);
        setHighlightAddress(false);
        setAddressError("");
      }

      setAddressView("list");
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
      setAddressView("list");
    }
  };

  const handleApplyCouponCode = async (couponCode: string) => {
    try {
      await applyCoupon({ couponCode }).unwrap();
      triggerGetSummary();
    } catch (err: any) {
      const errorMessage =
        err?.data?.Message || "Invalid or ineligible coupon code.";
      showConfirm({
        title: "Invalid Coupon",
        message: errorMessage,
        type: "error",
        confirmText: "Try Another",
      });
    }
  };

  const handleRemoveAppliedCoupon = async () => {
    try {
      await triggerRemoveCoupon().unwrap();
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleApplyLoyaltyPoints = async (pointsToUse: number) => {
    try {
      await applyRewardPoints({ pointsToUse }).unwrap();
      triggerGetSummary();
    } catch (err: any) {
      const errorMessage =
        err?.data?.Message || "Something Went wrong try again later.";
      showConfirm({
        title: "PetLand Points",
        message: errorMessage,
        type: "error",
        confirmText: "Try Another",
      });
    }
  };

  const handleRemoveLoyaltyPoints = async () => {
    try {
      await triggerRemoveRewards().unwrap();
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleClaimFreeGiftOption = async (rewardProduct: any) => {
    if (!rewardProduct?.productId) return;
    try {
      await selectFreeGift({ productId: rewardProduct.productId }).unwrap();
      setShowGiftAnimation(true);
      setTimeout(() => setShowGiftAnimation(false), 2500);
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleRemoveClaimedGift = async () => {
    try {
      await triggerRemoveGift().unwrap();
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleUpdateDonationAmount = async (amount: number) => {
    try {
      await updateDonation({
        isDonationEnabled: amount > 0,
        donationAmount: amount,
      }).unwrap();
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleApplyWalletBalance = async (customAmount?: number) => {
    try {
      const maxRedeemable = summaryResponse?.wallet?.maximumRedeemable || 0;
      const available = summaryResponse?.wallet?.availableBalance || 0;
      const upperLimit = Math.min(maxRedeemable, available);

      // Fall back to upper limit if custom amount isn't provided or exceeds the limit
      const amountToApply = customAmount
        ? Math.min(customAmount, upperLimit)
        : upperLimit;

      if (amountToApply <= 0) return;

      await applyWallet({ amount: amountToApply }).unwrap();
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleRemoveWalletBalance = async () => {
    try {
      await triggerRemoveWallet().unwrap();
      triggerGetSummary();
    } catch (err: any) {
      console.error(err);
    }
  };

  const handleSubscriptionChange = async (
    cartItemId: number,
    billingCycle: number,
    interval: number
  ) => {
    const request = {
      billingCycle,
      interval,
    }
    try {
      await updateCartSubscription({
        cartItemId,
        data: request
      }).unwrap();

      console.log("Subscription updated", {
        cartItemId,
        billingCycle,
        interval,
      });
      triggerGetSummary();

      // // Refresh cart after successful update
      // await fetchLatestCart();

    } catch (error) {
      console.error(
        "Failed to update subscription:",
        error
      );
    }
  };
  const handlePetPurchaseTypeChange = async (
    petCartItemId: number,
    purchaseType: number
  ) => {
    try {
      const request = {
        purchaseType
      }
      await updatePetPurchaseType({
        cartItemId: petCartItemId,
        data: request,
      }).unwrap();
      triggerGetSummary();
      console.log("Pet purchase type updated", {
        petCartItemId,
        purchaseType,
      });


    } catch (error) {
      console.error(
        "Failed to update pet payment type:",
        error
      );
    }
  };

  // Secure Handshake execution setup triggered for adding wallet credits
  const handleAddWalletMoney = async (amount: number, offerId?: number) => {
    try {
      const topUpPayload = {
        walletTopupOfferId: offerId || null,
        customAmount: Number(amount),
      };

      const response = await addTopupOffer(topUpPayload).unwrap();

      if (response?.paymentUrl && response?.transactionReference) {
        sessionStorage.setItem(
          "wallet_topup_tx_ref",
          response.transactionReference,
        );
        sessionStorage.setItem("wallet_topup_amount", String(response.amount));
        sessionStorage.setItem(
          "wallet_topup_credit",
          String(response.walletCredit),
        );

        window.location.href = response.paymentUrl;
      } else {
        alert(
          "Could not initialize secure wallet top-up gateway portal parameters.",
        );
      }
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.data?.message || "Failed to initialize deposit payload.";
      showConfirm({
        title: "Top-Up Failed",
        message: errorMessage,
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleProductClick = (product: ProductItem) => {
    navigate(`/productpage/customerneproductpage/${product.ProductId}`, {
      state: { product },
    });
  };

  const handleCheckoutValidation = () => {
    // No address selected
    if (!activeSelectedAddress || addressView === "form") {
      // Scroll smoothly to the Address section
      addressSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // Highlight the section
      setHighlightAddress(true);
      setAddressError("Please select or add a delivery address.");

      // Remove highlight after 2 seconds
      setTimeout(() => {
        setHighlightAddress(false);
        setAddressError("");
      }, 5000);

      return;
    }

    // Address exists
    handleProceedToPaymentGateway();
  };

  const handleProceedToPaymentGateway = async () => {
    if (totalItemsCount === 0 || !activeSelectedAddress) return;

    if (isSubmittingCheckout) return;

    setIsSubmittingCheckout(true);
    try {
      const checkoutPayload = {
        addressId: activeSelectedAddress.id,
        paymentMethod: "",
        notes: "",
      };

      const checkoutResponse =
        await processToCheckout(checkoutPayload).unwrap();

      const orderId = checkoutResponse.orderId;

      if (!orderId) {
        showConfirm({
          title: "Checkout Failed",
          message: "Could not process your checkout request. Please try again.",
          type: "error",
          confirmText: "Ok",
        });
        return;
      }

      //--------------------------------------------------
      // No online payment required
      //--------------------------------------------------
      if (!checkoutResponse.requiresOnlinePayment) {
        const paymentResult = await completeInternalPayment({
          orderId,
          paymentMethod: "Wallet",
        }).unwrap();

        const ref = paymentResult?.transactionReference;

        navigate(
          `/cartpage/CustomerPaymentSuccessScreen?ref=${encodeURIComponent(ref)}`,
        );
        return;
      }

      //--------------------------------------------------
      // Online payment required
      //--------------------------------------------------

      navigate("/cartpage/customerpaymentpage", {
        state: {
          orderId,
          pricing,
          selectedAddress: activeSelectedAddress,
        },
      });
    } catch (err: any) {
      showConfirm({
        title: "Info",
        message:
          err?.data?.Message ??
          err?.data?.message ??
          "Something went wrong. Please try again later.",
        type: "warning",
        confirmText: "Ok",
      });
    }
  };

  const handleGridAddToCart = async (p: ProductItem) => {
    try {
      const cartPayload = {
        productId: Number(p.ProductId),
        quantity: 1,
      };

      await createCart(cartPayload).unwrap();
      await triggerGetSummary();
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        "Could not increase quantity.";
      showConfirm({
        title: "",
        message: errorMessage,
        type: "error",
        confirmText: "Ok",
      });
    }
  };
  const handleGuestEmailVerificationRequired = (email: string) => {
    console.log(
      "Guest address created. Starting EMAIL OTP verification:",
      email,
    );

    setGuestVerificationEmail(email);
    setIsGuestVerification(true);

    setLoginMessage("Please verify your email address to continue checkout.");

    setShowLoginModal(true);
  };
  // const handleGuestVerificationRequired = (phoneNumber: string) => {
  //   console.log(
  //     "Guest address created. Starting OTP verification:",
  //     phoneNumber,
  //   );

  //   setGuestVerificationPhone(phoneNumber);
  //   setIsGuestVerification(true);

  //   setLoginMessage("Please verify your phone number to continue checkout.");

  //   setShowLoginModal(true);
  // };

  const handleExistingAccount = (message?: string) => {
    console.log("🔥🔥🔥 handleExistingAccount CALLED");
    console.log("🔥 MESSAGE RECEIVED:", message);
    setIsGuestVerification(false);
    // setGuestVerificationPhone("");
    setGuestVerificationEmail("");
    const finalMessage =
      message ||
      "An account already exists with these details. Please sign in to continue.";

    console.log("🔥 SETTING LOGIN MESSAGE:", finalMessage);

    setLoginMessage(finalMessage);

    console.log("🔥 SETTING SHOW LOGIN MODAL = TRUE");

    setShowLoginModal(true);

    console.log("🔥 handleExistingAccount FINISHED");
  };

  const handleLoginSuccess = async () => {
    setShowLoginModal(false);

    setIsGuestVerification(false);
    //setGuestVerificationPhone("");
    setGuestVerificationEmail("");

    try {
      const addressesRefresh = await triggerGetSavedAddresses().unwrap();

      const addressDataList = addressesRefresh?.data || [];

      if (addressDataList.length > 0) {
        const targetAddress =
          addressDataList.find((a: any) => a.isDefault) ||
          addressDataList[addressDataList.length - 1];

        await selectAddressMapping({
          addressId: targetAddress.id,
        }).unwrap();

        setActiveSelectedAddress(targetAddress);

        setHighlightAddress(false);
        setAddressError("");
        setAddressView("list");
      }

      // Important after Guest → Customer conversion
      await triggerGetSummary();
    } catch (err) {
      console.error("Failed to refresh checkout after login:", err);
    }
  };
  const handleAddressError = (title: string, message: string) => {
    addToast("error", title, message);
  };

  return (
    <div className="py-4 relative text-left box-border selection:bg-[#A8D97B]/30 max-w-7xl mx-auto">
      {isGlobalProcessing && (
        <div className="fixed inset-0 bg-black/10 backdrop-blur-xs z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-4 flex items-center gap-3 shadow-xl border border-slate-100">
            <Loader2 className="w-4 h-4 animate-spin text-[#2F5E2E]" />
            <span className="text-xs font-bold text-slate-800 tracking-wide">
              Updating order details...
            </span>
          </div>
        </div>
      )}

      <div className="px-4 lg:px-10 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        {/* LEFT PANEL */}
        <div className="w-full lg:w-[60%] space-y-6">
          {/* DELIVERY ADDRESS */}
          <div
            ref={addressSectionRef}
            className={`
    bg-white
    rounded-2xl
    border
    p-4
    shadow-3xs
    space-y-3
    transition-all
    duration-300
    ${highlightAddress
                ? "border-red-500 ring-4 ring-red-100 shadow-lg"
                : "border-slate-200"
              }
  `}
          >
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <div className="flex items-center gap-2 text-slate-900 font-bold text-xs uppercase tracking-wider">
                <MapPin size={15} className="text-[#2F5E2E]" />
                <h2>Delivery Address</h2>
              </div>
              {addressView === "list" && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => triggerGetSavedAddresses()}
                    className="text-[11px] text-[#2F5E2E] font-black flex items-center gap-1 hover:underline cursor-pointer"
                  >
                    <RefreshCw size={10} /> Refresh
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAddressId(undefined);
                      setAddressView("form");
                    }}
                    className="text-[11px] text-[#2F5E2E] font-bold hover:underline shrink-0 cursor-pointer"
                  >
                    Add Address
                  </button>
                </div>
              )}
            </div>

            {addressError && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-xs font-medium text-red-600 flex items-center gap-2">
                  <MapPin size={14} />
                  {addressError}
                </p>
              </div>
            )}
            <ToastContainer toasts={toasts} onClose={removeToast} />
            {addressView === "list" ? (
              <>
                {activeSelectedAddress ? (
                  <div className="space-y-3">
                    {/* Active Selected Address Card */}
                    <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/50 relative border-l-4 border-l-[#2F5E2E] flex justify-between items-start gap-4">
                      <div className="space-y-1 pr-2">
                        <p className="font-bold text-gray-900 text-xs flex items-center gap-1.5">
                          <span>{activeSelectedAddress.fullName}</span>
                          <span className="text-[9px] bg-emerald-50 text-emerald-700 font-black border border-emerald-100 px-1.5 py-0.5 rounded-md uppercase">
                            Selected
                          </span>
                        </p>
                        <p className="text-[11px] text-slate-600 leading-tight">
                          {activeSelectedAddress.addressLine1},{" "}
                          {activeSelectedAddress.city}, UAE
                        </p>
                        <p className="text-[11px] font-mono text-slate-500 font-bold">
                          {activeSelectedAddress.phoneNumber}
                        </p>
                      </div>

                      {/* Pencil Edit Button for Active Address */}
                      <button
                        onClick={() => {
                          setSelectedAddressId(activeSelectedAddress.id);
                          setAddressView("form");
                        }}
                        title="Edit Address"
                        className="p-1.5 text-slate-500 hover:text-[#2F5E2E] hover:bg-emerald-50 rounded-lg transition-colors border border-transparent hover:border-emerald-200 shrink-0 cursor-pointer"
                      >
                        <Edit2 size={14} />
                      </button>
                    </div>

                    {/* Quick Switch Badges List */}
                    {savedAddressesList.length > 1 && (
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider pl-0.5">
                          Quick Switch Address:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {savedAddressesList.map((address: IAddressData) => {
                            const isCurrent =
                              address.id === activeSelectedAddress.id;
                            return (
                              <div
                                key={address.id}
                                className={`px-3 py-1.5 rounded-xl border transition-all text-[11px] font-medium flex items-center gap-2 cursor-pointer shadow-3xs ${isCurrent
                                  ? "bg-emerald-50 border-emerald-500 text-emerald-900 font-bold"
                                  : "bg-white border-slate-200 text-slate-700 hover:border-[#2F5E2E]"
                                  }`}
                              >
                                <div
                                  onClick={() =>
                                    handleSelectAlternativeAddress(address)
                                  }
                                  className="flex items-center gap-1.5"
                                >
                                  {address.addressType === "office" ? (
                                    <Briefcase size={11} />
                                  ) : (
                                    <Home size={11} />
                                  )}
                                  <span className="truncate max-w-[120px]">
                                    {address.fullName}
                                  </span>
                                </div>

                                {/* Pencil icon on each badge */}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedAddressId(address.id);
                                    setAddressView("form");
                                  }}
                                  title="Edit"
                                  className="text-slate-400 hover:text-[#2F5E2E] p-0.5 rounded transition-colors"
                                >
                                  <Edit2 size={11} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <AddressForm
                    addressId={selectedAddressId}
                    onCancel={() => setAddressView("list")}
                    onSuccess={handleFormSuccessCompletion}
                    onError={handleAddressError}
                    // onGuestVerificationRequired={
                    //   handleGuestVerificationRequired
                    // }
                    onGuestEmailVerificationRequired={handleGuestEmailVerificationRequired}
                    onExistingAccount={handleExistingAccount}
                  />
                )}
              </>
            ) : (
              <AddressForm
                addressId={selectedAddressId}
                onCancel={() => setAddressView("list")}
                onSuccess={handleFormSuccessCompletion}
                onError={handleAddressError}
                // onGuestVerificationRequired={handleGuestVerificationRequired}
                onGuestEmailVerificationRequired={handleGuestEmailVerificationRequired}
                onExistingAccount={handleExistingAccount}
              />
            )}
          </div>

          {/* ITEM REVIEW SECTION */}
          <div className="space-y-4">
            <p className="text-sm font-black text-slate-900 tracking-tight flex items-center gap-1.5 pl-1">
              <ShoppingCart size={15} className="text-[#2F5E2E]" />
              <span>Review Items ({totalItemsCount})</span>
            </p>

            {totalItemsCount === 0 ? (
              <div className="border rounded-2xl border-dashed p-12 text-center text-xs text-slate-400 font-bold bg-white italic shadow-3xs">
     {(error as any)?.data?.Message ||
  (error as any)?.data?.message ||
  "Your checkout bag is currently empty."}
              </div>
            ) : (
              /* max-h-[880px] allows 8 full product cards to fit vertically before showing the scrollbar */
              <div
                className={
                  totalItemsCount > 9
                    ? "max-h-[1100px] overflow-y-auto pr-1.5 space-y-3 scrollbar-thin scrollbar-thumb-slate-200"
                    : "max-h-none overflow-hidden space-y-3"
                }
              >
                <div className="grid gap-3 grid-cols-1">
                  {selectedFreeGifts.map((gift, i) => (
                    <ItemsComp
                      key={`gift-selected-${gift.productId || i}`}
                      brandName="CLAIMED REWARD GIFT"
                      productTitle={gift.productName}
                      productImage={gift.imageUrl}
                      price="FREE"
                      originalPrice={undefined}
                      quantity={1}
                      variantDetails="Promotional Item"
                      isReward={true}
                      onRemove={handleRemoveClaimedGift}
                      onIncrement={undefined}
                      onDecrement={undefined}
                    />
                  ))}

                  {bundleItems.map((bundle: IBundle, bIdx) => (
                    <div
                      key={`bundle-card-${bundle.bundleId || bIdx}`}
                      className="w-full border border-slate-200 bg-slate-50/20 rounded-2xl p-3.5 space-y-3 shadow-2xs relative"
                    >
                      <div className="flex items-start justify-between border-b border-slate-100 pb-2 gap-4">
                        <div className="flex gap-2.5 items-center">
                          <div className="text-left space-y-0.5">
                            <span className="inline-flex items-center gap-1 text-[9px] text-[#2F5E2E] bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded font-black uppercase tracking-wider">
                              <Layers size={9} /> Promo Bundle Package
                            </span>
                            <h4 className="text-xs font-black text-slate-800 leading-tight">
                              {bundle.name || "Special Multi-Deal Bundle"}
                            </h4>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-right">
                            <p className="text-xs font-black text-slate-900 font-mono">
                              AED {bundle.bundlePrice || bundle.total}
                            </p>
                            {bundle.savingAmount > 0 && (
                              <p className="text-[9px] text-emerald-600 font-bold">
                                Save AED {bundle.savingAmount}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (bundle.items?.length) {
                                handleRemoveBundleGroup(bundle);
                              }
                            }}
                            className="p-1.5 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600 transition"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-2 pl-2 border-l-2 border-slate-200/60 ml-2">
                        {bundle.items?.map(
                          (subItem: IBundleItem, sIdx: number) => (
                            <div
                              key={`sub-${subItem.cartItemId || sIdx}`}
                              className="bg-white/80 rounded-xl border border-slate-100 p-2 flex items-center justify-between gap-3 text-left"
                            >
                              <div className="flex items-center gap-2 truncate">
                                <img
                                  src={subItem.imageUrl}
                                  alt=""
                                  className="w-8 h-8 rounded-lg border object-cover shrink-0"
                                />
                                <div className="truncate">
                                  <p className="text-[11px] font-bold text-slate-700 truncate">
                                    {subItem.productName}
                                  </p>
                                  <p className="text-[9px] text-slate-400 font-medium truncate">
                                    {subItem.variantName}
                                  </p>
                                </div>
                              </div>
                              <span className="text-[10px] font-black text-slate-500 font-mono shrink-0 bg-slate-50 border px-1.5 py-0.5 rounded-md">
                                QTY: {subItem.quantity || 1}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  ))}

                  {productItems
                    .filter((item) => !item.isBundleItem)
                    .map((product) => (
                      <ItemsComp
                        key={`prod-${product.cartItemId}`}
                        brandName={
                          product.isSubscription
                            ? "Autoship Product"
                            : "Product"
                        }
                        productTitle={product.productName}
                        productImage={product.imageUrl}
                        price={`AED ${product.total}`}
                        originalPrice={
                          product.unitSalePrice > 0 &&
                            product.unitSalePrice < product.unitPrice
                            ? `AED ${product.unitPrice *
                            product.quantity
                            }`
                            : undefined
                        }
                        quantity={product.quantity}
                        variantDetails={product.variantName}
                        isReward={false}
                        onRemove={() =>
                          handleRemoveProductItem(
                            product.cartItemId
                          )
                        }
                        onIncrement={() =>
                          handleIncreaseQuantity(
                            product.cartItemId
                          )
                        }
                        onDecrement={() =>
                          handleDecreaseQuantity(
                            product.cartItemId,
                            product.quantity
                          )
                        }
                        isSubscription={product.isSubscription}
                        billingCycle={product.billingCycle}
                        interval={product.interval}
                        onSubscriptionChange={(
                          billingCycle,
                          interval
                        ) =>
                          handleSubscriptionChange(
                            product.cartItemId,
                            billingCycle,
                            interval
                          )
                        }

                      />
                    ))}
                  {petItems.map((pet) => (
                    <ItemsComp
                      key={`pet-${pet.petCartItemId}`}
                      brandName="Pet Asset"
                      productTitle={pet.petName}
                      productImage={pet.productImage}
                      price={`AED ${Number(pet.amount).toFixed(2)}`}
                      variantDetails={pet.breed}
                      onRemove={() =>
                        handleRemovePetItem(pet.petCartItemId)
                      }
                      isPet={true}

                      // Total actual pet price
                      totalAmount={Number(pet.originalPrice || 0)}

                      // Already existing remaining amount
                      remainingAmount={Number(pet.remainingAmount || 0)}

                      purchaseType={pet.purchaseType}

                      onPetPurchaseTypeChange={(purchaseType: number) =>
                        handlePetPurchaseTypeChange(
                          pet.petCartItemId,
                          purchaseType
                        )
                      }
                    />
                  ))}

                  {serviceItems.map((svc) => (
                    <ItemsComp
                      key={`svc-${svc.cartItemId}`}
                      brandName="Service Booking"
                      productTitle={svc.serviceName}
                      productImage={svc.petImage}
                      price={`AED ${svc.price}`}
                      deliveryDate={svc.petName}
                      variantDetails={`${svc.bookingDate?.split("T")[0]
                        } (${svc.startTime?.substring(0, 5)})`}
                      onRemove={() => handleRemoveServiceItem(svc.cartItemId)}
                    />
                  ))}

                  {forcedItems.map((forcedProd, i) => (
                    <ItemsComp
                      key={`forced-${forcedProd.productId || i}`}
                      brandName="REQUIRED FOR CHECKOUT"
                      productTitle={forcedProd.productName}
                      productImage={forcedProd.imageUrl}
                      price={
                        forcedProd.salePrice === 0 || forcedProd.price === 0
                          ? "FREE"
                          : `AED ${forcedProd.salePrice || forcedProd.price}`
                      }
                      originalPrice={undefined}
                      quantity={1}
                      variantDetails={forcedProd.variantName || "Standard"}
                      isReward={true}
                      onRemove={undefined}
                      onIncrement={undefined}
                      onDecrement={undefined}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT SIDEBAR */}
        <div className="w-full lg:w-[40%] px-4 py-2 flex flex-col gap-4">
          {summaryResponse?.claimableGifts &&
            summaryResponse?.claimableGifts.length >= 1 && (
              <FreeClaimComponent
                totalPrice={pricing?.subTotal || 0}
                claimedRewardIds={productItems
                  .filter((item) => item.isFreeItem)
                  .map((item) => item.productVariantId)}
                onClaim={handleClaimFreeGiftOption}
                availableGifts={summaryResponse?.claimableGifts}
                showCelebration={showGiftAnimation}
              />
            )}

          {totalItemsCount != 0 && (
            <>
              {" "}
              {summaryResponse?.isCouponEnabled && (
                <CouponCard
                  availableCoupons={summaryResponse?.availableCoupons}
                  activeCouponCode={
                    summaryResponse?.availableCoupons?.find(
                      (c: any) => c.isApplied,
                    )?.couponCode || null
                  }
                  onCouponSuccess={handleApplyCouponCode}
                  onRemoveCoupon={handleRemoveAppliedCoupon}
                />
              )}
            </>
          )}

          {totalItemsCount != 0 && (
            <>
              {" "}
              {summaryResponse?.donation?.isEnabled && (
                <DonateCard
                  isActive={summaryResponse?.donation?.isEnabled}
                  currentAmount={summaryResponse?.donation?.donationAmount}
                  onSelect={handleUpdateDonationAmount}
                />
              )}
            </>
          )}

          {totalItemsCount != 0 && (
            <>
              {summaryResponse?.rewardPoints?.isEnabled && (
                <RewardPointsCard
                  totalPoints={
                    summaryResponse?.rewardPoints?.availablePoints || 0
                  }
                  maxUsablePoints={
                    summaryResponse?.rewardPoints?.maximumRedeemPoints || 0
                  }
                  appliedPoints={
                    summaryResponse?.rewardPoints?.appliedPoints || 0
                  }
                  discountAmount={
                    summaryResponse?.rewardPoints.discountAmount || 0
                  }
                  minimumRedeemPoints={
                    summaryResponse?.rewardPoints.minimumRedeemPoints || 0
                  }
                  onApply={handleApplyLoyaltyPoints}
                  onRemove={handleRemoveLoyaltyPoints}
                />
              )}
            </>
          )}

          {totalItemsCount != 0 && (
            <>
              {summaryResponse?.wallet.isEnabled && (
                <WalletCard
                  availableBalance={summaryResponse.wallet.availableBalance}
                  appliedAmount={summaryResponse.wallet.appliedAmount}
                  maximumRedeemable={summaryResponse.wallet.maximumRedeemable}
                  canRedeem={summaryResponse.wallet.canRedeem}
                  isApplied={summaryResponse.wallet.isApplied}
                  onApply={(amount) => handleApplyWalletBalance(amount)}
                  onRemove={handleRemoveWalletBalance}
                  onOpenTopUpPortal={() => triggerGetTopUpOffers()}
                  topUpPackages={topUpOffersList}
                  isPackagesLoading={isFetchingOffers}
                  onAddMoney={handleAddWalletMoney}
                  isLoading={
                    isApplyingWallet || isRemovingWallet || isInitializingTopUp
                  }
                />
              )}
            </>
          )}
          {totalItemsCount != 0 && (
            <>
              {" "}
              <OrderSummary
                totalMrp={pricing?.subTotal || 0}
                couponDiscount={pricing?.couponDiscount || 0}
                rewardDiscount={pricing?.rewardPointDiscount || 0}
                walletDiscount={pricing?.walletDiscount || 0}
                platformFee={pricing?.platformFee || 0}
                smallOrderFee={pricing?.smallOrderFee || 0}
                deliveryFee={pricing?.deliveryFee || 0}
                donation={pricing?.donationAmount || 0}
                taxAmount={pricing?.taxAmount || 0}
                grandTotal={pricing?.totalAmount || 0}
                gatewayAmount={pricing?.gatewayAmount}
                remainingAmount={pricing?.remainingAmount || 0}
                showButton={false}
              />
            </>
          )}

          <div className="mt-4 px-2 text-center">
            <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
              By placing your order, you agree to Petland's{" "}
              <button
                type="button"
                onClick={() => navigate("/termsAndConditions")}
                className="text-[#2F5E2E] font-medium hover:underline"
              >
                Terms and Conditions
              </button>
              {", "}
              <button
                type="button"
                onClick={() => navigate("/privacyPolicy")}
                className="text-[#2F5E2E] font-medium hover:underline"
              >
                Privacy Policy
              </button>
              {", "}
              <button
                type="button"
                onClick={() => navigate("/shippingPolicy")}
                className="text-[#2F5E2E] font-medium hover:underline"
              >
                Shipping Policy
              </button>
              {", "}
              <button
                type="button"
                onClick={() => navigate("/cancellationPolicy")}
                className="text-[#2F5E2E] font-medium hover:underline"
              >
                Cancellation Policy
              </button>
              {" and "}
              <button
                type="button"
                onClick={() => navigate("/returnAndExchange")}
                className="text-[#2F5E2E] font-medium hover:underline"
              >
                Return & Exchange Policy
              </button>
              .
            </p>
          </div>
        </div>
      </div>

      {/* FOOTER RAILS & SCROLL RECOMMENDS */}
      <div className="px-5">
        {similarProducts.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-slate-900 font-black text-xs uppercase tracking-wide px-1">
              <ShoppingBag size={14} className="text-[#2F5E2E]" />
              <h3>Similar Products</h3>
            </div>
            {/* Directly render ProductGridSection - no outer overflow wrappers */}
            <ProductGridSection
              products={similarProducts}
              handleProductPage={handleProductClick}
              handleAddToCart={handleGridAddToCart}
              handleIncrementToCart={undefined}
              handleDecrementFromCart={undefined}
              displayMode="carousel"
            />
          </div>
        )}

        {sponsoredProducts.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-slate-900 font-black text-xs uppercase tracking-wide px-1">
              <Sparkles size={14} className="text-[#2F5E2E]" />
              <h3>Sponsored Offers</h3>
            </div>
            <ProductGridSection
              products={sponsoredProducts}
              handleProductPage={handleProductClick}
              handleAddToCart={handleGridAddToCart}
              handleIncrementToCart={undefined}
              handleDecrementFromCart={undefined}
              displayMode="carousel"
            />
          </div>
        )}

        {recommendedProducts.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 text-slate-900 font-black text-xs uppercase tracking-wide px-1">
              <ShoppingCart size={14} className="text-[#2F5E2E]" />
              <h3>Petland Recommended</h3>
            </div>
            <ProductGridSection
              products={recommendedProducts}
              handleProductPage={handleProductClick}
              handleAddToCart={handleGridAddToCart}
              handleIncrementToCart={undefined}
              handleDecrementFromCart={undefined}
              displayMode="carousel"
            />
          </div>
        )}

        {suggestedBundles.length > 0 && (
          <div className="space-y-4 pt-6">
            <div className="flex items-center gap-2 px-1">
              <div className="p-1.5 bg-emerald-50 rounded-lg border border-emerald-100/50 flex items-center justify-center">
                <Layers size={14} className="text-[#2F5E2E] stroke-[2.5]" />
              </div>
              <div className="text-left">
                <h3 className="text-xs font-black text-slate-900 tracking-wide uppercase">
                  Frequently Bought Together
                </h3>
                <p className="text-[10px] text-slate-400 font-medium">
                  Get these matching essential combos at a special package
                  discount
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {suggestedBundles.map((b: IBundleSuggestion, bIdx) => {
                const productsList = b.products || [];
                const originalPriceValue = b.originalPrice || 0;
                const bundleSalePrice = b.bundlePrice || originalPriceValue;

                const discountPercent =
                  originalPriceValue > 0 && bundleSalePrice < originalPriceValue
                    ? Math.round(
                      ((originalPriceValue - bundleSalePrice) /
                        originalPriceValue) *
                      100,
                    )
                    : 0;

                return (
                  <div
                    key={b.bundleId || bIdx}
                    className="bg-slate-50/60 border border-slate-200/80 rounded-2xl p-5 w-full text-left shadow-3xs hover:border-emerald-500/20 transition-all duration-300"
                  >
                    <h4 className="font-black text-slate-800 text-xs md:text-sm uppercase tracking-wide mb-5 border-b border-dashed pb-2 border-slate-200">
                      {b.name || "Special Multi-Buy Offer Deal"}
                    </h4>

                    <div className="w-full overflow-x-auto scrollbar-none">
                      <div className="flex items-center gap-4 md:gap-6 min-w-max py-2 px-0.5 justify-start">
                        {productsList.map((item: any, index: number) => {
                          if (!item) return null;
                          return (
                            <React.Fragment key={item.productId || index}>
                              <div className="flex flex-col items-center justify-center text-center flex-shrink-0 w-28 sm:w-32 md:w-36 self-center">
                                <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-4xs w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center p-2.5">
                                  <img
                                    src={item.imageUrl || b.imageUrl}
                                    alt={item.name}
                                    className="max-w-full max-h-full object-contain"
                                  />
                                </div>
                                <p className="text-[10px] sm:text-[11px] mt-3 text-center font-bold text-slate-700 line-clamp-2 min-h-[32px] max-w-full leading-tight">
                                  {item.name || b.name}
                                </p>
                              </div>
                              {index < productsList.length - 1 && (
                                <div className="p-1.5 bg-slate-200/60 rounded-full border shrink-0 flex items-center justify-center self-center mb-8">
                                  <Plus className="w-3.5 h-3.5 text-slate-600 stroke-[3.5]" />
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}

                        <div className="p-1.5 bg-slate-200/60 rounded-full border shrink-0 flex items-center justify-center self-center mb-8">
                          <Equal className="w-3.5 h-3.5 text-slate-600 stroke-[3.5]" />
                        </div>

                        <div className="flex flex-col items-start justify-center gap-2 pl-2 flex-shrink-0 self-center mb-8">
                          <div className="flex flex-col">
                            <span className="text-[9px] uppercase font-black text-slate-400 tracking-wider">
                              Bundle Price
                            </span>
                            <div className="flex items-baseline gap-1.5 mt-0.5">
                              <span className="text-[#2F5E2E] font-black text-base md:text-lg font-mono">
                                AED {bundleSalePrice.toFixed(2)}
                              </span>
                              {originalPriceValue > bundleSalePrice && (
                                <span className="line-through text-slate-400 text-[10px] md:text-xs font-mono">
                                  AED {originalPriceValue.toFixed(2)}
                                </span>
                              )}
                            </div>
                          </div>

                          {b.alreadyAdded ? (
                            <span className="inline-flex items-center justify-center bg-emerald-50 text-emerald-800 font-black border border-emerald-200 rounded-full text-[10px] px-4 py-1.5 uppercase tracking-wider">
                              Already in Cart
                            </span>
                          ) : (
                            <CommonButton
                              onClick={() => handleAddBundleToCart(b.bundleId)}
                              text="Add Combo"
                              variant="filled"
                              rounded="full"
                              className="bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white px-4 text-[10px] h-8 font-black uppercase tracking-wide cursor-pointer"
                            />
                          )}

                          {discountPercent > 0 && (
                            <p className="text-[9px] font-black text-rose-600 uppercase bg-rose-50 border border-rose-100 px-2 py-0.5 rounded-md tracking-wider">
                              SAVE {discountPercent}% OFF
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-10 right-4 z-50">
        <CommonButton
          text="Proceed to Payment"
          variant="filled"
          rounded="full"
          icon={<ForwardIcon size={16} className="stroke-[2.5]" />}
          iconPosition="right"
          disabled={
            isSubmittingCheckout || isGlobalProcessing || totalItemsCount === 0
          }
          onClick={handleCheckoutValidation}
          className="bg-[#2F5E2E] text-white font-black px-6 py-3 shadow-md"
        />
      </div>
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
        }}
        onLoginSuccess={handleLoginSuccess}
        message={loginMessage}
        guestVerification={isGuestVerification}
        // guestPhoneNumber={guestVerificationPhone}
        guestEmail={guestVerificationEmail}
        ShowGuestClick={false}
      />
    </div>
  );
};

export default CustomerCart;
